/**
 * Labels, tones and small pieces shared by the admin screens. Admin copy can name internals (servers, cells,
 * Talos) because only the founder sees it; it is still British English and plain.
 */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Badge, Meter, Tooltip, type Tone } from "@/design";
import type {
  AdminArea,
  AdminAttentionItem,
  AdminNodeRole,
  AdminNodeStatus,
  AdminRef,
  AdminResourceUse,
  AdminWorkspaceFlag,
  AdminWorkspaceStatus,
  Change,
  IncidentSeverity,
  IncidentStatus,
  WorkspacePlan,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatBytes } from "@/lib/format";

export interface Meta {
  label: string;
  tone: Tone;
  explain?: string;
  pulse?: boolean;
}

export function Pill({ meta, size = "sm", className }: { meta: Meta; size?: "sm" | "md"; className?: string }) {
  const b = (
    <Badge tone={meta.tone} variant="dot" size={size} className={cn(meta.pulse && "[&>span:first-child]:animate-pulse", className)}>
      {meta.label}
    </Badge>
  );
  return meta.explain ? <Tooltip content={meta.explain}>{b}</Tooltip> : b;
}

export const nodeStatus: Record<AdminNodeStatus, Meta> = {
  ready: { label: "Ready", tone: "ok", explain: "Healthy and taking new work." },
  not_ready: { label: "Not ready", tone: "danger", explain: "The node agent reports a problem. Workloads may be moving off it." },
  cordoned: { label: "Cordoned", tone: "warn", explain: "Keeps running what it has, but nothing new is placed here." },
  draining: { label: "Draining", tone: "warn", pulse: true, explain: "Workloads are being moved to other servers." },
  provisioning: { label: "Installing", tone: "info", pulse: true, explain: "Ordered and being installed. It joins the cell when the key server approves it." },
  unreachable: { label: "Unreachable", tone: "danger", pulse: true, explain: "No heartbeat and no answer to probes." },
};

export const nodeRole: Record<AdminNodeRole, string> = {
  controlplane: "Control plane",
  worker: "App server",
  edge: "Edge",
  keys: "Key server",
  observability: "Observability",
  burst: "Burst",
};

export const workspaceStatus: Record<AdminWorkspaceStatus, Meta> = {
  active: { label: "Active", tone: "ok" },
  trial: { label: "Trial", tone: "info" },
  past_due: { label: "Past due", tone: "warn", explain: "An invoice is overdue. Apps keep running while the payment provider retries." },
  suspended: { label: "Suspended", tone: "danger", explain: "Stopped by a platform admin. Data is kept." },
  cancelled: { label: "Cancelled", tone: "idle", explain: "Closing or closed. Data is kept until the purge date, then crypto-shredded." },
};

export const planLabel: Record<WorkspacePlan, string> = { trial: "Trial", hobby: "Hobby", pro: "Pro", business: "Business", dedicated: "Dedicated" };

export const flagMeta: Record<AdminWorkspaceFlag, Meta> = {
  special_category_data: { label: "Health data", tone: "ai", explain: "Has a project marked special-category (health or care) data. Extra care with access and support." },
  over_cap: { label: "At cap", tone: "danger", explain: "Reached its spend cap. Non-production apps are asleep." },
  near_cap: { label: "Near cap", tone: "warn", explain: "Over 80% of its spend cap this month." },
  payment_failed: { label: "Payment failed", tone: "danger", explain: "An invoice is overdue or a card was declined." },
  abuse_report: { label: "Abuse report", tone: "danger", explain: "Someone reported this workspace." },
  poor_safety_grade: { label: "Grade D or E", tone: "warn", explain: "At least one project has a poor Safety Centre grade." },
  trial_ending: { label: "Trial ending", tone: "info", explain: "Trial ends within 7 days." },
  owner_without_passkey: { label: "Owner has no passkey", tone: "warn", explain: "The owner signs in without a passkey. Account recovery is harder and phishing easier." },
  idle: { label: "Idle", tone: "idle", explain: "No deploys or usage in 30 days." },
};

export const incidentSeverity: Record<IncidentSeverity, Meta> = {
  sev1: { label: "Sev 1", tone: "danger", explain: "Widespread outage or data at risk." },
  sev2: { label: "Sev 2", tone: "danger", explain: "Major customer impact." },
  sev3: { label: "Sev 3", tone: "warn", explain: "Partial or one-customer impact." },
  sev4: { label: "Sev 4", tone: "neutral", explain: "No customer impact yet." },
};

export const incidentStatus: Record<IncidentStatus, Meta> = {
  open: { label: "Open", tone: "danger" },
  investigating: { label: "Investigating", tone: "warn", pulse: true },
  mitigated: { label: "Mitigated", tone: "info" },
  resolved: { label: "Resolved", tone: "ok" },
  closed: { label: "Closed", tone: "idle" },
};

export const changeStatus: Record<Change["status"], Meta> = {
  proposed: { label: "Proposed", tone: "neutral", explain: "An agent suggested it. Nothing has run." },
  denied: { label: "Denied", tone: "idle", explain: "Policy or the reviewer said no. Nothing ran." },
  awaiting_review: { label: "With reviewer", tone: "info", pulse: true, explain: "A second model is checking the plan." },
  awaiting_approval: { label: "Needs you", tone: "warn", explain: "Tier 3: only you can approve it, with your passkey." },
  executing: { label: "Running", tone: "info", pulse: true },
  succeeded: { label: "Done", tone: "ok" },
  failed: { label: "Failed", tone: "danger" },
  rolled_back: { label: "Rolled back", tone: "warn" },
};

export const tierMeta: Record<number, Meta> = {
  0: { label: "Tier 0", tone: "neutral", explain: "Read only." },
  1: { label: "Tier 1", tone: "ok", explain: "Safe and reversible. Agents run it on their own." },
  2: { label: "Tier 2", tone: "info", explain: "Runs after a second model reviews the plan." },
  3: { label: "Tier 3", tone: "warn", explain: "Needs you, with your passkey." },
};

export const severityTone: Record<AdminAttentionItem["severity"], Tone> = { critical: "danger", warning: "warn", info: "info" };

/** Where an attention item's button goes. */
export function attentionLink(a: Pick<AdminAttentionItem, "area" | "target_id">): { to: string; params?: Record<string, string>; search?: Record<string, string> } {
  const t = a.target_id ?? undefined;
  const area: AdminArea = a.area;
  switch (area) {
    case "nodes":
      return t ? { to: "/admin/servers/$node", params: { node: t } } : { to: "/admin/servers" };
    case "workspaces":
      return t ? { to: "/admin/tenants/$workspaceId", params: { workspaceId: t } } : { to: "/admin/tenants" };
    case "users":
      return t ? { to: "/admin/accounts/$userId", params: { userId: t } } : { to: "/admin/accounts" };
    case "incidents":
      return { to: "/admin/incidents", search: t ? { incident: t } : {} };
    case "ai_ops":
      return { to: "/admin/ai-ops", search: t ? { change: t } : {} };
    case "operations":
      return { to: "/admin/operations" };
    case "finance":
      return { to: "/admin/money" };
    case "usage":
      return { to: "/admin/usage" };
    case "signups":
      return { to: "/admin/signups" };
    default:
      return { to: "/admin" };
  }
}

/* ------------------------------------------------------------ resource use */

export const vcpu = (millicores: number) => `${(millicores / 1000).toLocaleString("en-GB", { maximumFractionDigits: millicores >= 10_000 ? 0 : 1 })} vCPU`;

export function pctOf(part: number | null | undefined, whole: number): number {
  return part == null || !whole ? 0 : Math.round((part / whole) * 100);
}

/** Live use of one resource on one server or cell. Nothing is reserved, so the bar shows use, not requests. */
export function UseBar({ use, kind, className, compact }: { use: AdminResourceUse; kind: "cpu" | "memory" | "disk"; className?: string; compact?: boolean }) {
  const fmt = kind === "cpu" ? vcpu : (n: number) => formatBytes(n, 0);
  const value = use.used ?? 0;
  const max = use.allocatable || use.total;
  const label = use.used == null ? `${fmt(max)}; live use not reported yet` : `${fmt(value)} of ${fmt(max)} ${kind === "disk" ? "used" : "in use"}`;
  return (
    <div className={cn("min-w-0", className)}>
      {!compact && (
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
          <span className="font-medium text-muted">{kind === "cpu" ? "CPU" : kind === "memory" ? "Memory" : "Disk"}</span>
          <span className="tabular text-subtle">{pctOf(value, max)}%</span>
        </div>
      )}
      <Meter size="sm" value={value} max={max} thresholds={{ warn: 0.8, danger: 0.9 }} label={label} />
      {!compact && <div className="mt-1 truncate text-xs text-subtle">{label}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------- links */

export function TenantLink({ ws, className, children }: { ws: Pick<AdminRef, "id" | "name">; className?: string; children?: ReactNode }) {
  return (
    <Link to="/admin/tenants/$workspaceId" params={{ workspaceId: ws.id }} className={cn("font-medium text-text hover:text-accent-text hover:underline", className)}>
      {children ?? ws.name}
    </Link>
  );
}

export function AccountLink({ user, className }: { user: Pick<AdminRef, "id" | "name">; className?: string }) {
  return (
    <Link to="/admin/accounts/$userId" params={{ userId: user.id }} className={cn("font-medium text-text hover:text-accent-text hover:underline", className)}>
      {user.name}
    </Link>
  );
}

/** Copyable monospace id. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={cn("rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-muted", className)}>{children}</code>;
}
